import React, { useEffect, useMemo } from 'react';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { useState } from 'react';
import { useAccountStore } from '../../../../../components/Stores/accountStore';
import { useMaterialsStore } from '../../../../../components/Stores/MaterialsStore';
import ButtonDesigned from '../../../../../components/UI-Elements/Buttons/ButtonDesigned';
import { DateValueInputWithDateSwitch } from '../../../../../components/UI-Elements/Charts/DateValueInputMultiChart/DateValueInputWithDateSwitch';
import { getHighestAbsoluteValue } from '../../../../../utils/MonthYear/MonthYearValue';
import {
  getStartingDate,
  makeNegative,
  mapMaterialToCharts,
} from '../../FOIndexedClientExposure/FillOrderAdjustmentsUtils';
import { IFutureOrder, ISupplierExposure } from '@sp-types/FutureOrderType';
import { BackToBackMaterialCards } from './components/BackToBackMaterialCards';
import { createFutureOrder } from '../../../../../sp-firebase/futureOrderFB';
import { Product } from '../../../../../sp-types/productType';
import { mapProductToSupplierExposure } from '../../RawMaterialsExposure/RawMaterialsUtils';
import { Timestamp } from 'firebase/firestore';
import { getLastDate } from '../../AddNewFutureOrderUtils';
import { useCurrencyRates } from '@hooks/useCurrencyRates';
interface Props {
  products: Product[];
  setEditedFutureOrder: (_value: IFutureOrder) => void;
  currentFutureOrder: IFutureOrder;
  updateFutureOrderProperty: <K extends keyof IFutureOrder>(_property: K, _value: IFutureOrder[K]) => void;
  accountId: string;
  backPage: () => void;
  nextPage: () => void;
}

export const FOB2BMaterialBreakdown: React.FC<Props> = ({
  currentFutureOrder,
  setEditedFutureOrder,
  products,
  updateFutureOrderProperty,
  backPage,
  nextPage,
  accountId,
}) => {
  const { formulaColorDictionary } = useAccountStore();
  const { materialsMap } = useMaterialsStore();
  const { pricesMap } = useCurrencyRates();

  const { orderDetails, supplierExposure } = currentFutureOrder;
  const { pricePerUnit, productDeliveryCalendar } = orderDetails;
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  useEffect(() => {
    updateFutureOrderProperty(
      'supplierExposure',
      mapProductToSupplierExposure(products, currentFutureOrder, materialsMap),
    );
  }, [currentFutureOrder.orderDetails.productDeliveryCalendar]);

  return (
    <div className="flex flex-col h-full pt-7 overflow-y-auto overflow-x-hidden">
      <div className="flex flex-col flex-1 justify-between">
        <div className="w-full max-h-[20rem]">
          <BackToBackMaterialCards
            colorDict={formulaColorDictionary}
            currentFutureOrder={currentFutureOrder}
            setCurrentFutureOrder={setEditedFutureOrder}
            materialsMap={materialsMap}
          />
        </div>
        <div className="flex flex-col flex-1 /flexiblePlaceholder/">&nbsp;</div>
        <div>
          {productDeliveryCalendar && (
            <DateValueInputWithDateSwitch
              mainChart={{
                data: makeNegative(
                  productDeliveryCalendar.map((item) =>
                    pricePerUnit ? { ...item, value: item.value * pricePerUnit || 1 } : item,
                  ) || [],
                ),
                title: 'Product price',
                type: 'bar',
                transparent: true,
              }}
              isHideValueInput
              numberOfMonthsToShow={24}
              changeMonths={12}
              additionalCharts={mapMaterialToCharts(
                supplierExposure.map((item) => ({
                  ...item,
                  exposureCalendar: item.exposureCalendar.map((exposure) => ({
                    ...exposure,
                    value: exposure.value * pricesMap[item.formulaId],
                  })),
                })),
                materialsMap,
                formulaColorDictionary,
              )}
              startingDate={getStartingDate()}
              endDate={orderDetails.productDeliveryCalendar && getLastDate(orderDetails.productDeliveryCalendar)}
              suggestedMaxValue={
                productDeliveryCalendar && getHighestAbsoluteValue(productDeliveryCalendar) * 1.2 * (pricePerUnit || 1)
              }
              suggestedMinValue={
                productDeliveryCalendar && -getHighestAbsoluteValue(productDeliveryCalendar) * 1.2 * (pricePerUnit || 1)
              }
            />
          )}
        </div>
        <ButtonSection
          isButtonLoading={isButtonLoading}
          backPage={backPage}
          nextPage={() =>
            SaveAndRoute(
              setIsButtonLoading,
              currentFutureOrder.uuid || '',
              accountId,
              nextPage,
              updateFutureOrderProperty,
              currentFutureOrder.supplierExposure,
            )
          }
        />
      </div>
    </div>
  );
};

interface ButtonSectionProps {
  backPage: () => void;
  nextPage: () => void;
  isButtonLoading: boolean;
}

const ButtonSection: React.FC<ButtonSectionProps> = ({ backPage, nextPage, isButtonLoading }) => {
  return (
    <div className="flex justify-between sticky bottom-0 bg-white pt-2">
      <div className="flex gap-4 ">
        <ButtonDesigned
          style="outline"
          isFunctionLoading={isButtonLoading}
          disableButton={isButtonLoading}
          className=" flex items-center"
          onButtonClick={() => {
            backPage();
          }}
        >
          <img className=" my-auto rotate-180" title="Right" src="/assets/icons/flat/chevron-right_purple.svg"></img>
          Back
        </ButtonDesigned>
        <ButtonDesigned
          className="flex items-center"
          isFunctionLoading={isButtonLoading}
          disableButton={isButtonLoading}
          onButtonClick={() => {
            nextPage();
          }}
        >
          <div className="flex items-center">
            Next
            <img className=" invert  h-5" title="Right" src="/assets/icons/flat/chevron_right.svg"></img>
          </div>
        </ButtonDesigned>
      </div>
      <div className="flex">
        <ButtonDesigned disableButton={true} style="outline" onButtonClick={() => {}}>
          <ArticleOutlinedIcon className="text-blue-600 " />
          Go to policy
        </ButtonDesigned>
      </div>
    </div>
  );
};

const SaveAndRoute = async (
  setIsLoading: (_value: boolean) => void,
  futureOrderUuid: string,
  accountId: string,
  routeFunc: () => void,
  updateFutureOrderProperty: <K extends keyof IFutureOrder>(_property: K, _value: IFutureOrder[K]) => void,
  currentSupplierExposureState: ISupplierExposure[],
) => {
  setIsLoading(true);
  updateFutureOrderProperty('supplierExposure', currentSupplierExposureState);
  await createFutureOrder(accountId, {
    supplierExposure: currentSupplierExposureState,
    status: 'underwriting',
    updatedAt: Timestamp.now(),
    uuid: futureOrderUuid,
  });
  setIsLoading(false);
  routeFunc();
};
